// ============================================
// Player Feedback Page
// ============================================

import React from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Event, Session, EventTherapist } from '../../types';

interface ContextType {
  event: Event;
}

// Star rating component
function StarRating({
  rating,
  onRate,
  disabled,
}: {
  rating: number;
  onRate: (rating: number) => void;
  disabled: boolean;
}) {
  const [hoverRating, setHoverRating] = React.useState(0);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '0.5rem',
      }}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onRate(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          style={{
            background: 'none',
            border: 'none',
            cursor: disabled ? 'default' : 'pointer',
            fontSize: '2.5rem',
            padding: '0.25rem',
            transition: 'transform 0.1s ease',
            transform: (hoverRating >= star || rating >= star) ? 'scale(1.1)' : 'scale(1)',
          }}
        >
          <span
            style={{
              color: (hoverRating >= star || rating >= star)
                ? '#f59e0b'
                : 'var(--color-text-secondary)',
            }}
          >
            {(hoverRating >= star || rating >= star) ? '\u2605' : '\u2606'}
          </span>
        </button>
      ))}
    </div>
  );
}

export default function PlayerFeedback() {
  const { event } = useOutletContext<ContextType>();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = React.useState<Session | null>(null);
  const [therapist, setTherapist] = React.useState<EventTherapist | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Form state
  const [rating, setRating] = React.useState(0);
  const [amountPaid, setAmountPaid] = React.useState('');
  const [amountInitialized, setAmountInitialized] = React.useState(false);

  // Load session and therapist
  React.useEffect(() => {
    if (!sessionId) {
      setError('Session not found');
      setLoading(false);
      return;
    }

    const loadSession = async () => {
      try {
        const sessionDoc = await getDoc(doc(db, 'events', event.id, 'sessions', sessionId));
        if (!sessionDoc.exists()) {
          setError('Session not found');
          setLoading(false);
          return;
        }

        const sessionData = { id: sessionDoc.id, ...sessionDoc.data() } as Session;

        // Verify this session belongs to the current user
        if (sessionData.playerUid !== user?.uid) {
          setError('This is not your session');
          setLoading(false);
          return;
        }

        // Verify session is completed
        if (sessionData.status !== 'completed') {
          navigate(`/e/${event.id}/status`);
          return;
        }

        // Check if feedback already submitted
        if (sessionData.customerFeedbackAt) {
          navigate(`/e/${event.id}`);
          return;
        }

        setSession(sessionData);

        // Pre-fill amount with the bill amount
        if (!amountInitialized) {
          setAmountPaid(sessionData.billAmount.toFixed(0));
          setAmountInitialized(true);
        }

        // Load therapist info
        const therapistDoc = await getDoc(
          doc(db, 'events', event.id, 'therapists', sessionData.therapistId)
        );
        if (therapistDoc.exists()) {
          setTherapist({
            uid: therapistDoc.id,
            ...therapistDoc.data(),
          } as EventTherapist);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading session:', err);
        setError('Failed to load session');
        setLoading(false);
      }
    };

    loadSession();
  }, [event.id, sessionId, user, navigate]);

  const handleSubmit = async () => {
    // Validate based on event settings
    if (event.askCustomerRating && rating === 0) {
      setError('Please rate your experience');
      return;
    }

    if (event.askCustomerAmount && !amountPaid) {
      setError('Please enter the amount you paid');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const submitFeedback = httpsCallable(functions, 'submitCustomerFeedback');
      await submitFeedback({
        eventId: event.id,
        sessionId: session?.id,
        rating: event.askCustomerRating ? rating : null,
        amountPaid: event.askCustomerAmount ? parseFloat(amountPaid) : null,
      });

      // Navigate back to booking page
      navigate(`/e/${event.id}`);
    } catch (err: any) {
      console.error('Error submitting feedback:', err);
      setError(err.message || 'Failed to submit feedback');
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setSubmitting(true);
    try {
      const submitFeedback = httpsCallable(functions, 'submitCustomerFeedback');
      await submitFeedback({
        eventId: event.id,
        sessionId: session?.id,
        rating: null,
        amountPaid: null,
        skipped: true,
      });
      navigate(`/e/${event.id}`);
    } catch (err: any) {
      console.error('Error skipping feedback:', err);
      setError(err.message || 'Failed to skip');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="player-feedback loading" style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</p>
        <button
          onClick={() => navigate(`/e/${event.id}`)}
          className="btn btn-primary"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!session) return null;

  const therapistName = therapist?.publicDisplayName || therapist?.displayName || 'your therapist';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 120px)',
        padding: '1rem',
      }}
    >
      {/* Thank You Card */}
      <div
        style={{
          width: '100%',
          maxWidth: '360px',
          padding: '2rem 1.5rem',
          borderRadius: '16px',
          backgroundColor: 'var(--color-bg-secondary)',
          textAlign: 'center',
        }}
      >
        {/* Therapist Photo */}
        <div
          style={{
            width: '100px',
            height: '100px',
            margin: '0 auto 1.5rem',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '3px solid var(--color-primary)',
            backgroundColor: 'var(--color-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {therapist?.photoUrl && therapist.photoStatus === 'approved' ? (
            <img
              src={therapist.thumbnailUrl || therapist.photoUrl}
              alt={therapist.publicDisplayName || therapist.displayName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ fontSize: '2.5rem', color: 'var(--color-text-secondary)' }}>
              {(therapist?.publicDisplayName || therapist?.displayName || '?').charAt(0)}
            </span>
          )}
        </div>

        <h2 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>
          Session Complete!
        </h2>

        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
          Thank you for your massage with {therapistName}
        </p>

        {/* Session Details */}
        <div
          style={{
            padding: '1rem',
            backgroundColor: 'var(--color-bg)',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}
        >
          <div style={{ marginBottom: '0.5rem' }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Duration: </span>
            <strong>{session.totalMinutes} minutes</strong>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Bill: </span>
            <strong>
              {session.currencySnapshot} {session.billAmount.toFixed(0)}
            </strong>
          </div>
        </div>

        {/* Rating Section */}
        {event.askCustomerRating && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '0.75rem',
                fontWeight: 600,
              }}
            >
              How was your experience?
            </label>
            <StarRating
              rating={rating}
              onRate={setRating}
              disabled={submitting}
            />
          </div>
        )}

        {/* Amount Paid Section */}
        {event.askCustomerAmount && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="amountPaid"
              style={{
                display: 'block',
                marginBottom: '0.75rem',
                fontWeight: 600,
              }}
            >
              How much did you pay?
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
            >
              <span style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {session.currencySnapshot}
              </span>
              <input
                id="amountPaid"
                type="number"
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                placeholder="0"
                min="0"
                step="1"
                disabled={submitting}
                style={{
                  width: '120px',
                  padding: '0.75rem',
                  fontSize: '1.25rem',
                  textAlign: 'center',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg)',
                  color: '#fff',
                }}
              />
            </div>
          </div>
        )}

        {error && (
          <p style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '0.875rem' }}>
            {error}
          </p>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '1rem',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: 'var(--color-primary)',
            color: '#fff',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: submitting ? 'default' : 'pointer',
            opacity: submitting ? 0.5 : 1,
            marginBottom: '0.75rem',
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </button>

        {/* Skip Button */}
        <button
          onClick={handleSkip}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            backgroundColor: 'transparent',
            color: 'var(--color-text-secondary)',
            fontSize: '0.875rem',
            cursor: submitting ? 'default' : 'pointer',
          }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}
